import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Loader2, MessageCircle, Calendar, ExternalLink, Instagram } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import DonationProgress from "@/components/DonationProgress";
import CaseImage from "@/components/CaseImage";
import AnimalTypeIcon from "@/components/AnimalTypeIcon";
import DonateDialog from "@/components/dialogs/DonateDialog";
import { Button } from "@/components/ui/button";
import api, { photoUrl, getCasePhotoUrls, buildShareUrl, CANONICAL_URL } from "@/lib/api";
import { isDriveFolderLink } from "@/lib/driveUtils";
import { formatDate, daysUnderCareLabel, timeAtSanctuaryLabel } from "@/lib/dateUtils";
import { getPublicStatusLabel } from "@/lib/statusLabels";
import { generateStoryImage, shareOrDownloadStoryImage } from "@/lib/storyImageGenerator";
import { useToast } from "@/hooks/use-toast";

function setMetaTag(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function PublicCaseDetail() {
  const { slug } = useParams();
  const [caseItem, setCaseItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [generatingStory, setGeneratingStory] = useState(false);
  const [donateDialogOpen, setDonateDialogOpen] = useState(false);
  const [donationThankYou, setDonationThankYou] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api
      .get(`/public/cases/${slug}`)
      .then((res) => setCaseItem(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!caseItem) return;
    const cover = getCasePhotoUrls(caseItem)[0];
    const isSanctuary = caseItem.status === "Sanctuary Resident";
    const story = caseItem.sanctuary_story || caseItem.public_story || caseItem.case_history || "Support this rescue case with AMTM";
    const title = isSanctuary ? `Sponsor ${caseItem.animal_name} at our sanctuary ❤` : `Help ${caseItem.animal_name} recover ❤️`;
    document.title = isSanctuary ? `Sponsor ${caseItem.animal_name} | AMTM Rescue Hub` : `Help ${caseItem.animal_name} | AMTM Rescue Hub`;
    setMetaTag("og:title", title);
    setMetaTag("og:description", story);
    setMetaTag("og:url", `${CANONICAL_URL}${window.location.pathname}`);
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", story);
    if (cover) {
      setMetaTag("og:image", cover);
      setMetaTag("twitter:image", cover);
    }
  }, [caseItem]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-amtm-bg flex flex-col">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center text-amtm-textMuted" data-testid="public-case-not-found">
          This case is not available.
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (!caseItem) {
    return (
      <div className="min-h-screen bg-amtm-bg">
        <PublicHeader />
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-amtm-giveText" />
        </div>
      </div>
    );
  }

  const isSanctuary = caseItem.status === "Sanctuary Resident";
  const hero = getCasePhotoUrls(caseItem)[0];
  const showDriveFolderLink = isDriveFolderLink(caseItem.external_photo_link);
  const story = caseItem.sanctuary_story || caseItem.public_story || caseItem.case_history;
  const publicUpdates = [...(caseItem.treatment_updates || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
  // Shared links point to the backend's /api/share/case/{slug} route on the
  // canonical domain, which always runs on a real server (unlike the
  // static-built frontend in production) and serves proper Open Graph tags
  // before instantly redirecting real visitors to this same SPA page.
  const shareUrl = buildShareUrl(caseItem.slug);
  const shareMessage = isSanctuary
    ? `Sponsor ${caseItem.animal_name} at our sanctuary — ${shareUrl}`
    : `Help ${caseItem.animal_name} recover — ${shareUrl}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
  const hasDonationGoal = (caseItem.donation_needed || 0) > 0;
  const hasCareCost = (caseItem.monthly_care_cost || 0) > 0;
  const canSponsor = isSanctuary && hasCareCost && caseItem.sponsor_razorpay_link;
  const donationReceivedTotal = caseItem.donation_received_total ?? caseItem.donation_received ?? 0;

  const handleDonationSuccess = (updatedCase) => {
    if (updatedCase) setCaseItem(updatedCase);
    setDonationThankYou(true);
  };

  const handleShareStory = async () => {
    setGeneratingStory(true);
    try {
      const blob = await generateStoryImage(caseItem, hero, shareUrl);
      if (!blob) throw new Error("blob generation failed");
      await shareOrDownloadStoryImage(blob, `${caseItem.slug}-story.png`, shareUrl, toast);
    } catch {
      toast({ variant: "destructive", title: "Could not create story image", description: "Please try again." });
    } finally {
      setGeneratingStory(false);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <PublicHeader />

      <section className="max-w-4xl mx-auto px-6 md:px-8 py-10 md:py-16">
        <div className="rounded-3xl overflow-hidden shadow-lg mb-2 h-72 md:h-96" data-testid="public-case-hero">
          <CaseImage src={hero} alt={caseItem.animal_name} animalType={caseItem.animal_type} className="w-full h-full object-cover" />
        </div>
        {showDriveFolderLink && (
          <a
            href={caseItem.external_photo_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-photos-on-drive-link"
            className="inline-flex items-center gap-1.5 text-sm text-amtm-giveText font-semibold mb-6 hover:underline"
          >
            <ExternalLink className="h-4 w-4" /> View Photos on Drive
          </a>
        )}

        <div className="flex items-center justify-between mb-3 flex-wrap gap-2 mt-6">
          <h1 className="font-display text-4xl sm:text-5xl text-amtm-text" data-testid="public-case-name">{caseItem.animal_name}</h1>
          <span className="inline-flex items-center gap-1.5 bg-amtm-info/15 text-amtm-info px-3 py-1 rounded-full text-sm font-semibold">
            <AnimalTypeIcon animalType={caseItem.animal_type} className="h-4 w-4" /> {getPublicStatusLabel(caseItem.status)}
          </span>
        </div>

        <p className="flex items-center gap-1 text-sm text-amtm-textMuted mb-6" data-testid="public-case-admission-info">
          <Calendar className="h-4 w-4" />
          {isSanctuary
            ? timeAtSanctuaryLabel(caseItem.arrival_at_sanctuary_date)
            : `${caseItem.admission_date ? `Admitted on ${formatDate(caseItem.admission_date)} · ` : ""}${daysUnderCareLabel(caseItem)}`}
        </p>

        <p className="text-lg text-amtm-text leading-relaxed mb-8" data-testid="public-case-story">{story}</p>

        <div className="bg-amtm-surface border border-amtm-border rounded-2xl p-6 mb-8">
          {!isSanctuary && hasDonationGoal && (
            <DonationProgress
              needed={caseItem.donation_needed}
              received={donationReceivedTotal}
              donorCount={caseItem.donor_count}
              testId="public-case-donation-progress"
            />
          )}
          {isSanctuary && hasCareCost && (
            <p className="text-lg font-semibold text-amtm-text" data-testid="public-case-care-cost">
              ₹{caseItem.monthly_care_cost.toLocaleString("en-IN")}/month to care for {caseItem.animal_name}
            </p>
          )}
          {!isSanctuary && hasDonationGoal && caseItem.fundraising_status === "funded" && (
            <p className="text-sm font-semibold text-amtm-success bg-amtm-success/10 rounded-xl px-3.5 py-2.5 mt-3" data-testid="fundraiser-funded-banner">
              Fully funded — thank you! Extra gifts for {caseItem.animal_name} now go to our sanctuary feeding programme.
            </p>
          )}
          {!isSanctuary && hasDonationGoal && (
            <p className="text-xs text-amtm-textMuted mt-3" data-testid="fundraiser-surplus-note">
              If this rescue is funded beyond its goal, surplus funds go to the feeding programme for animals at our sanctuary.
            </p>
          )}
          {donationThankYou && (
            <div className="mt-3" data-testid="donation-thank-you-message">
              <p className="text-sm text-amtm-success font-semibold">
                Thank you! Your donation is helping {caseItem.animal_name} recover ❤
              </p>
              <p className="text-xs text-amtm-textMuted mt-1.5" data-testid="donation-thank-you-80g-note">
                AMTM is 80G certified — your donation is tax-deductible. To receive your 80G certificate, email us at{" "}
                <a href="mailto:purvi@amtmindia.org" className="text-amtm-giveText underline">
                  purvi@amtmindia.org
                </a>
                .
              </p>
            </div>
          )}
          <div className={`flex flex-col sm:flex-row gap-3 ${hasDonationGoal || hasCareCost ? "mt-5" : ""}`}>
            {!isSanctuary && hasDonationGoal && (
              <Button
                size="lg"
                className="flex-1 rounded-full bg-amtm-give text-amtm-giveText hover:bg-amtm-giveHover shadow-md"
                data-testid="donate-button"
                onClick={() => setDonateDialogOpen(true)}
              >
                <Heart className="h-5 w-5 mr-2" />
                {caseItem.fundraising_status === "funded" ? `Give an Extra Gift ❤️` : `Donate for ${caseItem.animal_name} ❤️`}
              </Button>
            )}
            {!isSanctuary && !hasDonationGoal && caseItem.razorpay_link && (
              <Button
                asChild
                size="lg"
                className="flex-1 rounded-full bg-amtm-give text-amtm-giveText hover:bg-amtm-giveHover shadow-md"
                data-testid="donate-button"
              >
                <a href={caseItem.razorpay_link} target="_blank" rel="noopener noreferrer">
                  <Heart className="h-5 w-5 mr-2" /> Donate for {caseItem.animal_name} ❤️
                </a>
              </Button>
            )}
            {canSponsor && (
              <Button
                asChild
                size="lg"
                className="flex-1 rounded-full bg-amtm-give text-amtm-giveText hover:bg-amtm-giveHover shadow-md"
                data-testid="sponsor-button"
              >
                <a href={caseItem.sponsor_razorpay_link} target="_blank" rel="noopener noreferrer">
                  <Heart className="h-5 w-5 mr-2" /> Sponsor {caseItem.animal_name} ❤
                </a>
              </Button>
            )}
            <Button asChild size="lg" variant="outline" className="flex-1 rounded-full border-amtm-success text-amtm-success hover:bg-amtm-success/10" data-testid="whatsapp-share-button">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-5 w-5 mr-2" /> Share on WhatsApp
              </a>
            </Button>
            <Button
              onClick={handleShareStory}
              disabled={generatingStory}
              size="lg"
              variant="outline"
              className="flex-1 rounded-full border-amtm-give text-amtm-giveText hover:bg-amtm-give/10"
              data-testid="instagram-story-share-button"
            >
              {generatingStory ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Instagram className="h-5 w-5 mr-2" />}
              Share to Instagram Story
            </Button>
          </div>
        </div>

        <div>
          <h2 className="font-display text-2xl text-amtm-text mb-4">{isSanctuary ? "Life at the Sanctuary" : "Recovery Journey"}</h2>
          {publicUpdates.length === 0 ? (
            <p className="text-amtm-textMuted" data-testid="public-case-no-updates">No updates yet — check back soon.</p>
          ) : (
            <div className="space-y-4" data-testid="public-case-updates-timeline">
              {publicUpdates.map((u) => (
                <div key={u.id} className="bg-amtm-surface border border-amtm-border rounded-2xl p-5">
                  <p className="text-xs text-amtm-textMuted mb-1.5">{formatDate(u.date)}</p>
                  <p className="text-amtm-text">{u.note}</p>
                  {u.photos?.length > 0 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto">
                      {u.photos.map((p, idx) => (
                        <CaseImage key={idx} src={photoUrl(p)} alt="update" className="h-28 w-28 rounded-xl object-cover shrink-0" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-12">
          <Link to="/cases" className="text-sm text-amtm-giveText font-semibold hover:underline" data-testid="back-to-all-cases-link">
            ← See other animals that need help
          </Link>
        </div>
      </section>

      <PublicFooter />
      {!isSanctuary && hasDonationGoal && (
        <DonateDialog
          open={donateDialogOpen}
          onOpenChange={setDonateDialogOpen}
          caseItem={caseItem}
          fallbackLink={caseItem.razorpay_link}
          onSuccess={handleDonationSuccess}
        />
      )}
    </div>
  );
}
